import { useAllAddresses } from 'app/network/address';
import {
  DEFAULT_SEARCH_RADIUS_IN_METERS,
  MAX_RADIUS_IN_METERS,
  useOnePreference,
  useUpsertOnePreference,
} from 'app/network/preferences';
import { useState } from 'react';
import type { LatLng } from 'react-native-maps';

export interface SearchCenter {
  latitude: number;
  longitude: number;
  radiusCenterLatitude: number;
  radiusCenterLongitude: number;
}

export function resolveSearchDistance(preferenceValue?: number) {
  if (typeof preferenceValue !== 'number' || !Number.isFinite(preferenceValue) || preferenceValue <= 0) {
    return DEFAULT_SEARCH_RADIUS_IN_METERS;
  }

  return Math.min(preferenceValue, MAX_RADIUS_IN_METERS);
}

export function useSearchCenter() {
  const { data: addresses } = useAllAddresses();
  const { data: searchDistancePreference } = useOnePreference('searchDistance');
  const { data: searchCenterLatPref } = useOnePreference('searchCenterLatitude');
  const { data: searchCenterLonPref } = useOnePreference('searchCenterLongitude');
  const { mutate: saveNewSearchCenterLatPref } = useUpsertOnePreference('searchCenterLatitude');
  const { mutate: saveNewSearchCenterLonPref } = useUpsertOnePreference('searchCenterLongitude');

  const address = addresses?.[0];
  const sourceKey = [
    address?.id,
    address?.latitude,
    address?.longitude,
    searchCenterLatPref?.value,
    searchCenterLonPref?.value,
  ].join(':');
  const defaultSearchCenter: SearchCenter = {
    latitude: address?.latitude ?? 0,
    longitude: address?.longitude ?? 0,
    radiusCenterLatitude: searchCenterLatPref?.value ?? address?.latitude ?? 0,
    radiusCenterLongitude: searchCenterLonPref?.value ?? address?.longitude ?? 0,
  };
  const [searchCenterState, setSearchCenterState] = useState({ sourceKey, value: defaultSearchCenter });
  let currentSearchCenterState = searchCenterState;
  if (searchCenterState.sourceKey !== sourceKey) {
    currentSearchCenterState = { sourceKey, value: defaultSearchCenter };
    setSearchCenterState(currentSearchCenterState);
  }
  const searchCenter = currentSearchCenterState.value;

  const setSearchRadiusCenter = (newCenter: LatLng) => {
    setSearchCenterState({
      sourceKey,
      value: {
        latitude: searchCenter.latitude,
        longitude: searchCenter.longitude,
        radiusCenterLatitude: newCenter.latitude,
        radiusCenterLongitude: newCenter.longitude,
      },
    });
  };

  const saveSearchCenter = (newCenter: LatLng) => {
    if (
      Math.abs(newCenter.latitude - searchCenter.radiusCenterLatitude) < 0.0001 &&
      Math.abs(newCenter.longitude - searchCenter.radiusCenterLongitude) < 0.0001
    ) {
      // Didn't change enough; ignoring
      return;
    }
    saveNewSearchCenterLatPref(newCenter.latitude);
    saveNewSearchCenterLonPref(newCenter.longitude);
    setSearchRadiusCenter(newCenter);
  };

  return {
    searchDistance: resolveSearchDistance(searchDistancePreference?.value),
    searchCenter,
    setSearchCenter: setSearchRadiusCenter,
    saveSearchCenter,
  };
}
